// ─────────────────────────────────────────────────────────────────────────────
// stores/registerStore.ts – Register Pinia Store
// ─────────────────────────────────────────────────────────────────────────────

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { Unsubscribe } from 'firebase/firestore';

import type { Register }       from '@/firebase/models';
import { RegisterStatus }      from '@/firebase/models';
import { subscribeToRegisters } from '@/services/printJobService';

export const useRegisterStore = defineStore('registers', () => {
  // ── State ──────────────────────────────────────────────────────────────────
  const registers = ref<Register[]>([]);
  const isLoading = ref(false);
  const error     = ref<string | null>(null);
  /** Haelt der Store gerade einen Listener (REQ-OPS-053 (a) (ii), OP-151)? */
  const isSubscribed = ref(false);

  let _unsubscribe: Unsubscribe | null = null;

  // ── Computed ───────────────────────────────────────────────────────────────
  const onlineRegisters  = computed(() =>
    registers.value.filter((r) => r.status === RegisterStatus.ONLINE),
  );
  const offlineRegisters = computed(() =>
    registers.value.filter((r) => r.status === RegisterStatus.OFFLINE),
  );
  const totalOnline = computed(() => onlineRegisters.value.length);

  // ── Actions ────────────────────────────────────────────────────────────────

  function subscribe(companyId: string): void {
    // Die vorige Anmeldung abraeumen — das bricht zugleich eine laufende
    // Rueckzugsstaffel ab (REQ-OPS-053 (d)).
    if (_unsubscribe) _unsubscribe();
    isLoading.value    = true;
    error.value        = null;
    isSubscribed.value = true;

    _unsubscribe = subscribeToRegisters(
      companyId,
      (data) => {
        registers.value = data;
        isLoading.value = false;
      },
      // Der Zustand bleibt WAHR (OP-151): Ladezustand faellt, das
      // „abonniert"-Merkmal wird zurueckgenommen, die Meldung ist gesetzt.
      (err) => {
        error.value        = `Fehler beim Laden der Kassen: ${err.message}`;
        isLoading.value    = false;
        isSubscribed.value = false;
      },
      // Abmeldung: kein Fehlzustand (REQ-OPS-053 (c)).
      () => {
        isLoading.value    = false;
        isSubscribed.value = false;
      },
      // Der Rueckweg (REQ-OPS-053 (a), Gegenrichtung): erster erfolgreicher
      // Schnappschuss nach einem Fehler — die Meldung faellt, das Merkmal steht.
      () => {
        error.value        = null;
        isSubscribed.value = true;
      },
    );
  }

  function unsubscribe(): void {
    _unsubscribe?.();
    _unsubscribe       = null;
    registers.value    = [];
    isSubscribed.value = false;
  }

  return {
    registers,
    onlineRegisters,
    offlineRegisters,
    totalOnline,
    isLoading,
    error,
    isSubscribed,
    subscribe,
    unsubscribe,
  };
});
